import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import { cloneElement, forwardRef } from 'react';
import {
  border,
  color,
  compose,
  flexbox,
  layout,
  position,
  space,
  system,
  typography,
} from 'styled-system';

import { forwardValidProp } from '../../utils/style';
import Flex from '../Flex';
import Spinner from '../Spinner';
import Text from '../Text';

const SIZES = ['size1', 'size2', 'size3', 'size4'];
const TYPES = [
  'primary',
  'secondary',
  'subtle',
  'store',
  'ghost',
  'like',
  'liked',
  'dislike',
  'disliked',
];
const HTML_MARKUP_OPTIONS = ['button', 'a'];

const mobileSizeMap = {
  size1: 'size2',
  size2: 'size3',
  size3: 'size4',
  size4: 'size4',
};

const iconsSizeMap = {
  size1: 'size1',
  size2: 'size1',
  size3: 'size2',
  size4: 'size2',
};

const baseStyles = ({ theme }) => {
  return css`
    align-items: center;
    border-width: 0.0125rem;
    border-radius: ${theme.border.radius.size2};
    border-style: solid;
    display: flex;
    font-weight: ${theme.typography.fontWeights.bold};
    justify-content: center;
    user-select: none;
    text-decoration: none;
    transition: ${theme.transitions.fast};
    width: fit-content;

    &:focus {
      border-color: ${theme.colors.button.default.borderFocused};
      outline: none;
    }
  `;
};

const textSizes = ({ theme, size }) => {
  const textSizeMap = {
    size1: css`
      font-size: ${theme.typography.texts[mobileSizeMap.size1].fontSize};
      line-height: ${theme.typography.texts[mobileSizeMap.size1].lineHeight};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.texts.size1.fontSize};
        line-height: ${theme.typography.texts.size1.lineHeight};
      }
    `,
    size2: css`
      font-size: ${theme.typography.texts[mobileSizeMap.size1].fontSize};
      line-height: ${theme.typography.texts[mobileSizeMap.size1].lineHeight};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.texts.size1.fontSize};
        line-height: ${theme.typography.texts.size1.lineHeight};
      }
    `,
    size3: css`
      font-size: ${theme.typography.texts[mobileSizeMap.size2].fontSize};
      line-height: ${theme.typography.texts[mobileSizeMap.size2].lineHeight};
      max-height: ${theme.space[6]};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.texts.size2.fontSize};
        line-height: ${theme.typography.texts.size2.lineHeight};
      }
    `,
    size4: css`
      font-size: ${theme.typography.texts[mobileSizeMap.size2].fontSize};
      line-height: ${theme.typography.texts[mobileSizeMap.size2].lineHeight};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.texts.size2.fontSize};
        line-height: ${theme.typography.texts.size2.lineHeight};
      }
    `,
  };

  return size && textSizeMap[size];
};

const StyledText = styled(Text)`
  ${textSizes}
  ${css`
    align-items: center;
    display: flex;
    justify-content: center;
  `}
  ${({ theme, mobileIcon }) =>
    mobileIcon &&
    css`
      display: none;

      ${theme.mediaQueries.md} {
        display: block;
      }
    `}
`;

const typeStyles = ({ theme, btnType, disabled, loading }) => {
  if (disabled) {
    return false;
  }

  const buttonTypes = {
    ghost: css`
      border-color: ${theme.colors.button.ghost.border};

      ${StyledText},
      svg {
        color: ${theme.colors.button.ghost.foreground};
      }

      &:hover {
        background-color: ${!loading && theme.colors.button.ghost.bgHover};
        border-color: ${!loading && theme.colors.button.ghost.borderHover};
      }
    `,
    like: css`
      border-color: ${theme.colors.button.like.border};

      svg {
        color: ${theme.colors.button.like.icon};
      }

      &:hover {
        background-color: ${!loading && theme.colors.button.like.bgHover};
        border-color: ${!loading && theme.colors.button.like.border};

        svg {
          color: ${!loading && theme.colors.button.like.iconHover};
        }
      }
    `,
    liked: css`
      border-color: ${theme.colors.button.like.border};

      svg {
        color: ${theme.colors.button.liked.icon};
      }

      &:hover {
        background-color: ${!loading && theme.colors.button.liked.bgHover};
        border-color: ${!loading && theme.colors.button.liked.border};
      }
    `,
    dislike: css`
      border-color: ${theme.colors.button.dislike.border};

      svg {
        color: ${theme.colors.button.dislike.icon};
      }

      &:hover {
        background-color: ${!loading && theme.colors.button.dislike.bg};
        border-color: ${!loading && theme.colors.button.dislike.border};

        svg {
          color: ${!loading && theme.colors.button.dislike.iconHover};
        }
      }
    `,
    disliked: css`
      border-color: ${theme.colors.button.disliked.border};

      svg {
        color: ${theme.colors.button.disliked.icon};
      }

      &:hover {
        background-color: ${!loading && theme.colors.button.disliked.bg};
        border-color: ${!loading && theme.colors.button.disliked.border};
      }
    `,
    store: css`
      background-color: ${theme.colors.button.store.bg};
      border-color: ${theme.colors.button.store.border};

      ${StyledText},
      svg {
        color: ${theme.colors.button.store.foreground};
      }

      &:hover {
        background-color: ${!loading && theme.colors.button.store.bgHover};
        border-color: ${!loading && theme.colors.button.store.borderHover};
      }
    `,
    primary: css`
      background-color: ${theme.colors.button.primary.bg};
      border-color: ${theme.colors.button.primary.border};

      ${StyledText},
      svg {
        color: ${theme.colors.button.primary.foreground};
      }

      &:hover {
        background-color: ${!loading && theme.colors.button.primary.bgHover};
        border-color: ${!loading && theme.colors.button.primary.borderHover};
      }
    `,
    secondary: css`
      background-color: ${theme.colors.button.secondary.bg};
      border-color: ${theme.colors.button.secondary.border};

      ${StyledText},
      svg {
        color: ${theme.colors.button.secondary.foreground};
      }

      &:hover {
        background-color: ${!loading && theme.colors.button.secondary.bgHover};
        border-color: ${!loading && theme.colors.button.secondary.borderHover};

        ${StyledText},
        svg {
          color: ${!loading && theme.colors.button.secondary.foregroundHover};
        }
      }
    `,
    subtle: css`
      background-color: ${theme.colors.button.subtle.bg};
      border-color: ${theme.colors.button.subtle.border};

      ${StyledText},
      svg {
        color: ${theme.colors.button.subtle.foreground};
      }

      &:hover {
        background-color: ${!loading && theme.colors.button.subtle.bgHover};
        border-color: ${!loading && theme.colors.button.subtle.borderHover};
      }
    `,
  };

  return btnType && buttonTypes[btnType];
};

const disabledStyles = ({ theme, disabled }) =>
  disabled &&
  css`
    background-color: ${theme.colors.button.disabled.bg};
    border-color: ${theme.colors.button.disabled.border};
    cursor: not-allowed;

    ${StyledText} {
      color: ${theme.colors.button.disabled.foreground};
    }
  `;

const fullStyles = ({ full }) =>
  full &&
  css`
    width: 100%;
  `;

const loadingStyles = ({ loading }) =>
  loading &&
  css`
    cursor: wait;
  `;

const buttonSizes = ({ theme, weight }) => {
  const buttonSizeMap = {
    size1: css`
      font-size: ${theme.typography.texts[mobileSizeMap.size1].fontSize};
      line-height: ${theme.typography.texts[mobileSizeMap.size1].lineHeight};
      padding: ${theme.space[3]};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.texts.size1.fontSize};
        line-height: ${theme.typography.texts.size1.lineHeight};
      }
    `,
    size2: css`
      font-size: ${theme.typography.texts[mobileSizeMap.size1].fontSize};
      line-height: ${theme.typography.texts[mobileSizeMap.size1].lineHeight};
      padding: ${theme.space[2]};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.texts.size1.fontSize};
        line-height: ${theme.typography.texts.size1.lineHeight};
      }
    `,
    size3: css`
      font-size: ${theme.typography.texts[mobileSizeMap.size2].fontSize};
      line-height: ${theme.typography.texts[mobileSizeMap.size2].lineHeight};
      padding: ${theme.space[2]};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.texts.size2.fontSize};
        line-height: ${theme.typography.texts.size2.lineHeight};
      }
    `,
    size4: css`
      font-size: ${theme.typography.texts[mobileSizeMap.size2].fontSize};
      line-height: ${theme.typography.texts[mobileSizeMap.size2].lineHeight};
      padding: ${theme.space[1]};

      ${theme.mediaQueries.lg} {
        font-size: ${theme.typography.texts.size2.fontSize};
        line-height: ${theme.typography.texts.size2.lineHeight};
      }
    `,
  };

  return weight && buttonSizeMap[weight];
};

const StyledButton = styled('button', {
  shouldForwardProp: (prop) => forwardValidProp(prop),
})`
  ${system({
    visibility: {
      property: 'visibility',
    },
  })}
  ${baseStyles}
  ${typeStyles}
  ${buttonSizes}
  ${disabledStyles}
  ${fullStyles}
  ${loadingStyles}
  ${compose(border, color, flexbox, layout, space, position, typography)}
`;

const Button = forwardRef(
  (
    {
      children,
      htmlType,
      icon,
      iconLeft,
      iconRight,
      mobileIcon,
      loading,
      size,
      type,
      ...rest
    },
    ref
  ) => (
    <StyledButton
      loading={loading || undefined}
      // `size` prop is used by styled-system layout
      // calling it weight is a workaround
      weight={size}
      ref={ref}
      type={htmlType}
      btnType={type}
      {...rest}
    >
      {iconLeft && !loading && (
        <Flex
          alignItems="center"
          display={mobileIcon ? ['none', null, 'flex'] : 'flex'}
        >
          {cloneElement(iconLeft, { size: iconsSizeMap[size] })}
        </Flex>
      )}
      {mobileIcon && (
        <Flex display={loading ? 'none' : [null, null, 'none']}>
          {cloneElement(mobileIcon, { size: iconsSizeMap[size] })}
        </Flex>
      )}
      {icon ? (
        <Flex display={mobileIcon ? ['none', null, 'flex'] : 'flex'}>
          {cloneElement(icon, { size: iconsSizeMap[size] })}
        </Flex>
      ) : (
        <StyledText
          as="span"
          whiteSpace="nowrap"
          size={size === 'size4' ? 'size3' : size}
          mobileIcon={mobileIcon}
          ml={iconLeft && 2}
          mr={(loading || iconRight) && 2}
          bold
        >
          {children}
        </StyledText>
      )}
      {iconRight && !loading && (
        <Flex display={mobileIcon ? ['none', null, 'flex'] : 'flex'}>
          {cloneElement(iconRight, { size: iconsSizeMap[size] })}
        </Flex>
      )}
      {loading && <Spinner size={size} />}
    </StyledButton>
  )
);

Button.SIZES = SIZES;
Button.TYPES = TYPES;
Button.HTML_MARKUP_OPTIONS = HTML_MARKUP_OPTIONS;
Button.displayName = 'Button';

Button.defaultProps = {
  as: 'button',
  children: null,
  disabled: false,
  full: false,
  href: null,
  htmlType: 'button',
  icon: null,
  iconLeft: null,
  iconRight: null,
  loading: false,
  mobileIcon: null,
  size: 'size1',
  type: 'primary',
  visibility: 'visible',
  onClick: () => {},
  onFocus: () => {},
  onMouseOver: () => {},
  onMouseLeave: () => {},
};

Button.propTypes = {
  /**
   * HTML markup to be rendered
   */
  as: PropTypes.oneOf(['button', 'a']),
  /**
   * Children to be rendered inside component
   */
  children: PropTypes.node,
  /**
   * Renders a disabled button
   */
  disabled: PropTypes.bool,
  /**
   * Button fills the parent component's width
   */
  full: PropTypes.bool,
  /**
   * Renders an icon as children
   */
  icon: PropTypes.shape(),
  /**
   * Renders an icon on the left side
   */
  iconLeft: PropTypes.shape(),
  /**
   * Renders an icon on the right side
   */
  iconRight: PropTypes.shape(),
  /**
   * `href` HTML attribute used when `as` prop is set to `a`
   */
  href: PropTypes.string,
  /**
   * `type` HTML attribute, default is 'button
   */
  htmlType: PropTypes.oneOf(['submit', 'reset', 'button', '']),
  /**
   * Set as loading state
   */
  loading: PropTypes.bool,
  /**
   * Renders Icon as the only children instead of any text on mobile
   */
  mobileIcon: PropTypes.shape(),
  /**
   * Renders button with one of the properties below
   */
  type: PropTypes.oneOf([
    'primary',
    'secondary',
    'subtle',
    'store',
    'ghost',
    'like',
    'dislike',
    'liked',
    'disliked',
  ]),
  /**
   * Renders a text with a determined size
   */
  size: PropTypes.oneOf(['size1', 'size2', 'size3', 'size4']),
  /**
   * Renders component with one of the following visibilities
   */
  visibility: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.oneOf(['visible', 'hidden', 'collapse']),
  ]),
  /**
   * Callback function on click
   */
  onClick: PropTypes.func,
  /**
   * Callback function on focus
   */
  onFocus: PropTypes.func,
  /**
   * Callback function on mouse over
   */
  onMouseOver: PropTypes.func,
  /**
   * Callback function on mouse leave
   */
  onMouseLeave: PropTypes.func,
  /**
   * Implements `styled-system` [color](https://styled-system.com/api/#color) utility
   */
  ...propTypes.color,
  /**
   * Implements `styled-system` [border](https://styled-system.com/api/#border) utility
   */
  ...propTypes.border,
  /**
   * Implements `styled-system` [flexbox](https://styled-system.com/api/#flexbox) utility
   */
  ...propTypes.flexbox,
  /**
   * Implements `styled-system` [layout](https://styled-system.com/api/#layout) utility
   */
  ...propTypes.layout,
  /**
   * Implements `styled-system` [space](https://styled-system.com/api/#space) utility
   */
  ...propTypes.space,
  /**
   * Implements `styled-system` [typography](https://styled-system.com/api/#typography) utility
   */
  ...propTypes.typography,
  /**
   * Implements `styled-system` [position](https://styled-system.com/api/#position) utility
   */
  ...propTypes.position,
};

export default Button;
